import type {
    ListProviderFacilitiesRequest,
    ListProviderProvidersRequest,
    ProviderFacility,
    ProviderProvider,
    ProviderType,
} from '../../api';
import { ROSTER_API_MAX_LIMIT, rosterApiSort } from './roster.support-mapping';
import type { RosterFacility, RosterListQuery, RosterProvider, RosterTab } from './roster.types';

/**
 * Translation between the Roster screen and the PROVIDER app's API (`/provider/provider`,
 * `/provider/facility`). Pure, like `roster.support-mapping.ts`, which it reuses where the two APIs
 * agree: the sort keys (every one the Roster uses exists on both), the 100-record cap, and the
 * merge of per-field search requests.
 *
 * Where they differ — probed on sandbox 2026-09-29 and read from the generated client:
 * - the lists are read-only: no create, update, disable or tag routes;
 * - no `filterActive`: nothing to send, and whether disabled records come back is not known;
 * - no `filterFirstLast`: a name is searched as `filterFirst` and `filterLast` separately, and a
 *   two-word search also as first AND last together;
 * - the records carry no `tags` and no `automation`.
 */

export type RosterProviderAppSearchFilter =
    | 'filterFirst'
    | 'filterLast'
    | 'filterOrganization'
    | 'filterName'
    | 'filterNpi'
    | 'filterAddressLines'
    | 'filterAddressCity';

/** One search request: the filters it sends, each with the text it matches (`LK`). */
export type RosterProviderAppSearch = Partial<Record<RosterProviderAppSearchFilter, string>>;

/**
 * The requests a search is run as. Different filters in one request are ANDed (measured on the
 * support API 2026-09-25; assumed here — `/provider/provider` answered 500 on the day it was
 * probed, so recheck it live), so each request carries one field — except the full-name one,
 * where AND is the point: "maya okafor" is first "maya" and last "okafor", which neither name
 * field holds alone.
 */
export function rosterProviderAppSearches(tab: RosterTab, search: string): RosterProviderAppSearch[] {
    const text = search.trim();
    if (/^[\d\s-]+$/.test(text)) {
        return [{ filterNpi: text.replace(/\D/g, '') }, { filterAddressLines: text }];
    }
    if (tab === 'facilities') {
        return [{ filterName: text }, { filterAddressLines: text }, { filterAddressCity: text }];
    }
    const searches: RosterProviderAppSearch[] = [
        { filterFirst: text },
        { filterLast: text },
        { filterOrganization: text },
        { filterAddressLines: text },
        { filterAddressCity: text },
    ];
    const words = text.split(/\s+/);
    if (words.length > 1) {
        searches.push({ filterFirst: words[0], filterLast: words.slice(1).join(' ') });
    }
    return searches;
}

/** A search as request filters: each value matched as a case-insensitive substring. */
export function rosterProviderAppSearchFilters(search: RosterProviderAppSearch): Record<string, string[]> {
    return Object.fromEntries(Object.entries(search).map(([filter, value]) => [filter, [`LK:${value}`]]));
}

export function rosterProviderAppProvidersRequest(query: RosterListQuery): ListProviderProvidersRequest {
    const { sort, order } = rosterApiSort('providers', query.sort);
    return {
        limit: clampLimit(query.limit),
        offset: query.offset,
        ...(query.providerType ? { filterProviderType: [`EQ:${query.providerType}`] } : {}),
        ...(sort
            ? {
                  sort: sort as ListProviderProvidersRequest['sort'],
                  order: order as ListProviderProvidersRequest['order'],
              }
            : {}),
    };
}

export function rosterProviderAppFacilitiesRequest(query: RosterListQuery): ListProviderFacilitiesRequest {
    const { sort, order } = rosterApiSort('facilities', query.sort);
    return {
        limit: clampLimit(query.limit),
        offset: query.offset,
        ...(sort
            ? {
                  sort: sort as ListProviderFacilitiesRequest['sort'],
                  order: order as ListProviderFacilitiesRequest['order'],
              }
            : {}),
    };
}

function clampLimit(limit: number): number {
    return Math.min(ROSTER_API_MAX_LIMIT, Math.max(1, limit));
}

/** No tags and no automation on this API — `tags` is empty and the capability says why. */
export function rosterProviderFromProviderApp(provider: ProviderProvider): RosterProvider {
    return {
        id: provider.id as string,
        type: provider.type as ProviderType,
        organization: provider.organization,
        first: provider.first,
        middle: provider.middle,
        last: provider.last,
        suffix: provider.suffix,
        npi: provider.npi,
        ein: provider.ein,
        ssn: provider.ssn,
        ptan: provider.ptan,
        license: provider.license,
        taxonomyCode: provider.taxonomyCode,
        taxonomyQualifier: provider.taxonomyQualifier,
        email: provider.email,
        phone: provider.phone,
        fax: provider.fax,
        tags: [],
        halt: !!provider.halt,
        addresses: provider.addresses ?? [],
        created: provider.created,
    };
}

export function rosterFacilityFromProviderApp(facility: ProviderFacility): RosterFacility {
    return {
        id: facility.id as string,
        name: facility.name,
        nickname: facility.nickname,
        description: facility.description,
        npi: facility.npi,
        ein: facility.ein,
        ssn: facility.ssn,
        other: facility.other,
        placeOfServiceCode: facility.placeOfServiceCode,
        tags: [],
        addresses: facility.addresses ?? [],
        created: facility.created,
    };
}
